import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import { calculateFinalPayableRounding } from "../runtime/commerce/final-payable-rounding";
import type { CustomerBenefitRuntimeItem } from "../runtime/gateway/customer-benefit-client";
import type { LoyaltyRewardSnapshot } from "../runtime/gateway/loyalty-redemption-client";
import {
  localCartSaleFingerprint,
  readLocalCart,
  setLocalCartPromotionClaims,
  setLocalCartQuote,
  type LocalCartDraft,
} from "../runtime/local-store/cart-state";
import { readActiveCustomerBenefits } from "../runtime/local-store/customer-benefit-runtime";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import {
  clearLocalLoyaltyRedemption,
  readLocalLoyaltyRedemption,
  saveLocalLoyaltyRedemption,
  saveLocalLoyaltyRewardPricing,
  type LocalLoyaltyRedemption,
  type LocalLoyaltyRewardPricing,
} from "../runtime/local-store/loyalty-redemption-state";
import { readPromotionRuntime } from "../runtime/local-store/promotion-runtime";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";
import {
  resolveLocalReservedReward,
  rewardFromCustomerBenefit,
} from "../runtime/loyalty/local-reward-resolver";
import { resolveLocalCustomerClaimPromotionsV1 } from "../runtime/promotion/local-customer-claim-resolver";
import {
  clearPromotionClaimSession,
  readPromotionClaimSession,
  setPromotionCustomerClaim,
  subscribePromotionClaimSession,
} from "../runtime/promotion/promotion-claim-session";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
  cart: LocalCartDraft | null;
  benefits: CustomerBenefitRuntimeItem[];
  redemption: LocalLoyaltyRedemption | null;
  staleRedemption: LocalLoyaltyRedemption | null;
};

type RewardPreview = {
  pricing: LocalLoyaltyRewardPricing | null;
  error: string | null;
};

function friendly(code: string | null | undefined) {
  if (!code) return "ใช้สิทธิ์ไม่สำเร็จ กรุณาลองใหม่";
  if (code.includes("POS_SALE_QUOTE_REQUIRED")) return "กำลังคำนวณบิล กรุณาลองอีกครั้ง";
  if (code.includes("FINAL_PAYABLE_SETTINGS_NOT_READY")) return "ข้อมูลคำนวณยอดชำระยังไม่พร้อม กรุณารอสักครู่";
  if (code.includes("NO_ELIGIBLE_ORDER_AMOUNT")) return "บิลนี้ไม่มีสินค้าที่ใช้รางวัลนี้ได้";
  if (code.includes("ELIGIBILITY_ALLOCATION_NOT_READY")) return "ยังคำนวณส่วนลดสำหรับบิลนี้ไม่ได้ กรุณาลองอีกครั้ง";
  if (code.includes("COMPATIBILITY_SNAPSHOT_REQUIRED") || code.includes("COMPATIBILITY_POLICY_NOT_READY")) return "ยังตรวจเงื่อนไขการใช้ร่วมกับโปรโมชั่นไม่ได้";
  if (code.includes("PROMOTION_CONFLICT")) return "รางวัลนี้ใช้ร่วมกับโปรโมชั่นที่กำลังใช้ในบิลไม่ได้";
  if (code.includes("PROMOTION_CLAIM_LOCAL_PROOF_NOT_READY")) return "สิทธิ์นี้ยังไม่พร้อมใช้แบบ Local กรุณาซิงก์ข้อมูลล่าสุด";
  if (code.includes("PROMOTION_CLAIM_CUSTOMER_MISMATCH") || code.includes("CUSTOMER_MISMATCH")) return "สิทธิ์นี้เป็นของสมาชิกคนอื่น กรุณาเลือกสมาชิกใหม่";
  if (code.includes("CURRENCY")) return "สกุลเงินของรางวัลไม่ตรงกับบิลนี้";
  if (code.includes("BENEFIT_NOT_SUPPORTED")) return "รางวัลประเภทนี้ยังใช้กับบิลนี้ไม่ได้";
  return "ใช้สิทธิ์ไม่สำเร็จ กรุณาลองใหม่";
}

function rewardLabel(reward: LoyaltyRewardSnapshot) {
  if (reward.benefit_type === "FIXED_AMOUNT_DISCOUNT") {
    const amount = Number(reward.benefit_document.amount ?? 0);
    return amount > 0 ? `ลด ${new Intl.NumberFormat("th-TH").format(amount)} บาท` : reward.name;
  }
  if (reward.benefit_type === "CATALOG_ITEM") return "รับสินค้าในร้าน";
  if (reward.benefit_type === "STOCK_ITEM") return "รับของรางวัล";
  return reward.name;
}

function promotionClaimLabel(item: CustomerBenefitRuntimeItem) {
  const snapshot = item.benefit_snapshot ?? {};
  const definition = snapshot.claim_definition;
  if (definition && typeof definition === "object") {
    const name = (definition as Record<string, unknown>).display_name;
    if (typeof name === "string" && name.trim()) return name;
  }
  const displayName = snapshot.display_name;
  if (typeof displayName === "string" && displayName.trim()) return displayName;
  return "สิทธิ์ส่วนลดอื่น";
}

function sourceRank(item: CustomerBenefitRuntimeItem) {
  return item.source_type === "LOYALTY_REWARD" ? 0 : 1;
}

function formatMoney(value: number, currencyCode: string) {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatBenefitTime(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function LoyaltyRedemptionRuntimeHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({
    terminal: null,
    session: null,
    cart: null,
    benefits: [],
    redemption: null,
    staleRedemption: null,
  });
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, RewardPreview>>({});
  const claimSession = useSyncExternalStore(
    subscribePromotionClaimSession,
    readPromotionClaimSession,
    readPromotionClaimSession,
  );

  useEffect(() => {
    const subscription = liveQuery(async () => {
      const terminal = await readTerminalCredential();
      const identity = terminal?.identity ?? null;
      const [session, cart] = await Promise.all([
        readLocalEmployeeSession(),
        identity ? readLocalCart(identity) : Promise.resolve(null),
      ]);
      const allRedemption = identity ? await readLocalLoyaltyRedemption(identity) : null;
      const redemption = identity && cart ? await readLocalLoyaltyRedemption(identity, cart.requestKey) : null;
      const benefits = identity && cart?.selectedMember
        ? await readActiveCustomerBenefits(identity, cart.selectedMember.customerId)
        : [];
      return {
        terminal,
        session,
        cart,
        benefits,
        redemption,
        staleRedemption: allRedemption && (!cart || allRedemption.cartRequestKey !== cart.requestKey) ? allRedemption : null,
      } satisfies RuntimeSnapshot;
    }).subscribe({
      next: setRuntime,
      error: () => setRuntime({ terminal: null, session: null, cart: null, benefits: [], redemption: null, staleRedemption: null }),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const update = () => setMountNode(document.querySelector(".sale-context-mount"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const terminal = runtime.terminal;
  const identity = terminal?.identity ?? null;
  const actor = runtime.session?.actor ?? null;
  const cart = runtime.cart;
  const redemption = runtime.redemption;
  const fingerprint = cart ? localCartSaleFingerprint(cart) : "";
  const pricingFresh = Boolean(redemption?.pricing && redemption.pricing.cartFingerprint === fingerprint);
  const sameScope = Boolean(actor && identity
    && actor.organization_id === identity.organization_id
    && actor.branch_id === identity.branch_id
    && actor.node_id === identity.node_id
    && actor.runtime_session_status === "ACTIVE");

  const benefits = useMemo(
    () => [...runtime.benefits].sort((a, b) => sourceRank(a) - sourceRank(b) || a.changed_at.localeCompare(b.changed_at)),
    [runtime.benefits],
  );
  const loyaltyBenefits = useMemo(() => benefits.filter((item) => item.source_type === "LOYALTY_REWARD"), [benefits]);
  const promotionBenefits = useMemo(() => benefits.filter((item) => item.source_type === "PROMOTION_CLAIM"), [benefits]);
  const activeBenefit = useMemo(
    () => redemption ? loyaltyBenefits.find((item) => item.source_instance_id === redemption.sourceInstanceId) ?? null : null,
    [loyaltyBenefits, redemption],
  );
  const activePromotionBenefit = useMemo(() => {
    const selection = claimSession.customerClaim;
    return selection
      ? promotionBenefits.find((item) => item.source_instance_id === selection.claimInstanceId) ?? null
      : null;
  }, [claimSession.customerClaim, promotionBenefits]);

  const baselinePayable = useMemo(() => {
    if (!cart?.lines.length || !cart.quote || !cart.commerceCalculation) return null;
    if (cart.quote.cartFingerprint !== localCartSaleFingerprint(cart)) return null;
    return calculateFinalPayableRounding(cart.quote.postPromotionTotal, cart.commerceCalculation);
  }, [cart]);

  useEffect(() => {
    if (!identity || !runtime.staleRedemption) return;
    void clearLocalLoyaltyRedemption(identity);
  }, [identity, runtime.staleRedemption]);

  useEffect(() => {
    if (!identity || !redemption) return;
    if (!cart?.lines.length || !cart.selectedMember || cart.selectedMember.customerId !== redemption.customerId) {
      void clearLocalLoyaltyRedemption(identity);
    }
  }, [cart, identity, redemption]);

  useEffect(() => {
    const selection = claimSession.customerClaim;
    if (!identity || !cart || !selection) return;
    const benefit = promotionBenefits.find((item) => item.source_instance_id === selection.claimInstanceId);
    const valid = Boolean(
      cart.lines.length
      && cart.selectedMember
      && cart.selectedMember.customerId === selection.customerId
      && benefit
      && benefit.customer_id === selection.customerId
      && benefit.promotion_version_id === selection.promotionVersionId,
    );
    if (!valid) {
      clearPromotionClaimSession();
      if (cart.claimedPromotionVersionIds.includes(selection.promotionVersionId)) {
        void setLocalCartPromotionClaims(
          identity,
          cart,
          cart.claimedPromotionVersionIds.filter((id) => id !== selection.promotionVersionId),
        );
      }
      return;
    }
    if (navigator.onLine) return;

    void (async () => {
      const promotionRuntime = await readPromotionRuntime(identity);
      const localQuoteFresh = Boolean(
        cart.quote
        && cart.quote.cartFingerprint === localCartSaleFingerprint(cart)
        && cart.quote.quoteSource === "LOCAL_PROMOTION_RUNTIME_V1"
        && cart.quote.promotionRuntimeRevision === promotionRuntime.revision,
      );
      if (localQuoteFresh) return;
      const resolution = resolveLocalCustomerClaimPromotionsV1({
        cart,
        promotions: promotionRuntime.promotions,
        promotionRuntimeRevision: promotionRuntime.revision,
        claimPromotionVersionId: selection.promotionVersionId,
      });
      await setLocalCartQuote(identity, cart, resolution.quote);
    })().catch((cause) => {
      setError(friendly(cause instanceof Error ? cause.message : "PROMOTION_CLAIM_LOCAL_QUOTE_FAILED"));
    });
  }, [cart, claimSession.customerClaim, identity, promotionBenefits]);

  const resolveBenefit = useCallback(async (benefit: CustomerBenefitRuntimeItem) => {
    if (!identity || !cart?.lines.length || !cart.selectedMember) throw new Error("LOYALTY_REWARD_CART_NOT_READY");
    const promotionRuntime = await readPromotionRuntime(identity);
    return resolveLocalReservedReward({ benefit, cart, promotionRuntimes: promotionRuntime.promotions });
  }, [cart, identity]);

  const repriceBoundReward = useCallback(async (benefit: CustomerBenefitRuntimeItem, value: LocalLoyaltyRedemption) => {
    if (!identity) return;
    const resolution = await resolveBenefit(benefit);
    await saveLocalLoyaltyRewardPricing(identity, value, resolution.pricing);
  }, [identity, resolveBenefit]);

  useEffect(() => {
    if (!identity || !cart || !redemption || pricingFresh || !cart.quote) return;
    if (!cart.lines.length || !cart.selectedMember || cart.selectedMember.customerId !== redemption.customerId) return;
    const benefit = loyaltyBenefits.find((item) => item.source_instance_id === redemption.sourceInstanceId);
    if (!benefit) {
      void clearLocalLoyaltyRedemption(identity);
      return;
    }
    void repriceBoundReward(benefit, redemption).catch((cause) => {
      setError(friendly(cause instanceof Error ? cause.message : "LOYALTY_REWARD_LOCAL_REPRICE_FAILED"));
    });
  }, [cart, identity, loyaltyBenefits, pricingFresh, redemption, repriceBoundReward]);

  useEffect(() => {
    if (!open || !identity || !cart?.lines.length || !cart.selectedMember || !cart.quote) {
      setPreviews({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const promotionRuntime = await readPromotionRuntime(identity);
      const next: Record<string, RewardPreview> = {};
      for (const benefit of loyaltyBenefits) {
        try {
          const resolution = resolveLocalReservedReward({ benefit, cart, promotionRuntimes: promotionRuntime.promotions });
          next[benefit.projection_id] = { pricing: resolution.pricing, error: null };
        } catch (cause) {
          next[benefit.projection_id] = {
            pricing: null,
            error: friendly(cause instanceof Error ? cause.message : "LOYALTY_REWARD_LOCAL_PREVIEW_FAILED"),
          };
        }
      }
      if (!cancelled) setPreviews(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [cart, identity, loyaltyBenefits, open]);

  async function confirmReward(benefit: CustomerBenefitRuntimeItem) {
    if (!identity || !cart?.lines.length || !cart.selectedMember) return;
    setBusy(true);
    setError(null);
    try {
      const resolution = await resolveBenefit(benefit);
      const local = await saveLocalLoyaltyRedemption(identity, {
        cartRequestKey: cart.requestKey,
        redemptionId: benefit.source_instance_id,
        sourceType: "LOYALTY_REWARD",
        sourceInstanceId: benefit.source_instance_id,
        projectionId: benefit.projection_id,
        customerId: benefit.customer_id,
        customerName: cart.selectedMember.customerName,
        rewardVersionId: resolution.reward.reward_version_id,
        pointCost: Number(benefit.point_cost_snapshot ?? resolution.reward.point_cost),
        reward: resolution.reward,
        reservationState: "RESERVED",
        validUntil: benefit.valid_until ?? null,
        pricing: resolution.pricing,
      });
      if (!local) throw new Error("LOYALTY_REWARD_LOCAL_BIND_FAILED");
      setOpen(false);
    } catch (cause) {
      setError(friendly(cause instanceof Error ? cause.message : "LOYALTY_REWARD_LOCAL_APPLY_FAILED"));
    } finally {
      setBusy(false);
    }
  }

  async function detachReward() {
    if (!identity) return;
    setBusy(true);
    setError(null);
    try {
      await clearLocalLoyaltyRedemption(identity);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPromotionClaim(benefit: CustomerBenefitRuntimeItem) {
    if (!identity || !cart?.lines.length || !cart.selectedMember || !benefit.promotion_version_id) return;
    if (benefit.customer_id !== cart.selectedMember.customerId) {
      setError(friendly("PROMOTION_CLAIM_CUSTOMER_MISMATCH"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Customer-bound Claim and STAFF_CLAIM share the one customer-selected lane.
      // Choosing a Member Web Claim replaces every older manual Staff selection.
      const nextCart = cart.claimedPromotionVersionIds.length
        ? await setLocalCartPromotionClaims(identity, cart, [])
        : cart;
      setPromotionCustomerClaim({
        claimInstanceId: benefit.source_instance_id,
        customerId: benefit.customer_id,
        promotionVersionId: benefit.promotion_version_id,
      });
      if (!navigator.onLine) {
        const promotionRuntime = await readPromotionRuntime(identity);
        const resolution = resolveLocalCustomerClaimPromotionsV1({
          cart: nextCart,
          promotions: promotionRuntime.promotions,
          promotionRuntimeRevision: promotionRuntime.revision,
          claimPromotionVersionId: benefit.promotion_version_id,
        });
        await setLocalCartQuote(identity, nextCart, resolution.quote);
      }
      setOpen(false);
    } catch (cause) {
      setError(friendly(cause instanceof Error ? cause.message : "PROMOTION_CLAIM_LOCAL_APPLY_FAILED"));
    } finally {
      setBusy(false);
    }
  }

  async function detachPromotionClaim() {
    const selection = claimSession.customerClaim;
    if (!identity || !cart || !selection) return;
    setBusy(true);
    setError(null);
    try {
      clearPromotionClaimSession();
      await setLocalCartPromotionClaims(
        identity,
        cart,
        cart.claimedPromotionVersionIds.filter((id) => id !== selection.promotionVersionId),
      );
    } finally {
      setBusy(false);
    }
  }

  if (!mountNode || !terminal || !identity || !actor || !cart || !sameScope) return null;

  const activeReward = redemption?.reward ?? null;
  const chipLabel = activeReward
    ? rewardLabel(activeReward)
    : activePromotionBenefit
      ? promotionClaimLabel(activePromotionBenefit)
      : cart.selectedMember && benefits.length
        ? `สิทธิ์ ${benefits.length}`
        : "สิทธิ์สมาชิก";

  return <>
    {createPortal(
      <button
        type="button"
        className={`sale-context-chip loyalty-redemption-chip ${activeReward || activePromotionBenefit || benefits.length ? "sale-context-chip--active" : ""}`}
        disabled={!cart.lines.length}
        onClick={() => { setOpen(true); setError(null); }}
      >
        <span aria-hidden="true">★</span>
        <b>{chipLabel}</b>
        {activeReward && !pricingFresh ? <em>กำลังคำนวณ</em> : null}
      </button>,
      mountNode,
    )}

    {open ? createPortal(
      <div className="sale-context-backdrop" role="presentation">
        <section className="sale-context-modal loyalty-redemption-modal" role="dialog" aria-modal="true" aria-labelledby="loyalty-redemption-title">
          <header>
            <div><small>MORE MEMBER</small><h2 id="loyalty-redemption-title">สิทธิ์ของสมาชิก</h2></div>
            <button type="button" onClick={() => setOpen(false)}>×</button>
          </header>

          {error ? <div className="sale-context-error">{error}</div> : null}
          {!cart.selectedMember ? (
            <div className="sale-context-empty">เลือกสมาชิกในบิลก่อนเพื่อดูสิทธิ์ที่พร้อมใช้</div>
          ) : benefits.length === 0 ? (
            <div className="sale-context-empty">สมาชิก {cart.selectedMember.customerName} ยังไม่มีสิทธิ์ที่พร้อมใช้</div>
          ) : (
            <>
              <div className="loyalty-redemption-customer">
                <small>สมาชิก</small>
                <strong>{cart.selectedMember.customerName}</strong>
                <span>ตรวจรายละเอียดก่อนยืนยันใช้สิทธิ์กับบิลนี้</span>
              </div>

              {redemption ? <section className="loyalty-redemption-current">
                <div>
                  <small>สิทธิ์ที่ใช้กับบิลนี้แล้ว</small>
                  <strong>{redemption.reward.name}</strong>
                  <span>ใช้ {new Intl.NumberFormat("th-TH").format(redemption.pointCost)} แต้ม · {rewardLabel(redemption.reward)}</span>
                  {activeBenefit?.valid_from ? <span>ลูกค้ากดแลกเมื่อ {formatBenefitTime(activeBenefit.valid_from)}</span> : null}
                  {activeBenefit?.valid_until ? <span>ใช้ได้ถึง {formatBenefitTime(activeBenefit.valid_until)}</span> : null}
                </div>
                <div className="loyalty-redemption-price">
                  {pricingFresh && redemption.pricing ? <>
                    <span>ส่วนลด {formatMoney(redemption.pricing.appliedDiscountAmount, redemption.pricing.currencyCode)}</span>
                    <strong>ยอดหลังใช้สิทธิ์ {formatMoney(redemption.pricing.totalAmount, redemption.pricing.currencyCode)}</strong>
                  </> : <strong>กำลังคำนวณสิทธิ์ใหม่</strong>}
                </div>
                <div className="loyalty-redemption-actions">
                  <button type="button" className="loyalty-redemption-release" disabled={busy} onClick={() => void detachReward()}>เอาสิทธิ์ออกจากบิล</button>
                </div>
              </section> : null}

              {!redemption ? <section className="loyalty-redemption-resolved">
                <div className="loyalty-redemption-rewards">
                  {loyaltyBenefits.map((benefit) => {
                    const reward = rewardFromCustomerBenefit(benefit);
                    if (!reward) return null;
                    const preview = previews[benefit.projection_id];
                    const redeemedAt = formatBenefitTime(benefit.valid_from);
                    const validUntil = formatBenefitTime(benefit.valid_until);
                    return <section key={benefit.projection_id} className="loyalty-redemption-current">
                      <div>
                        <small>สิทธิ์ส่วนลด</small>
                        <strong>{reward.name}</strong>
                        <span>{rewardLabel(reward)} · ใช้ {new Intl.NumberFormat("th-TH").format(Number(benefit.point_cost_snapshot ?? reward.point_cost))} แต้ม</span>
                        {redeemedAt ? <span>ลูกค้ากดแลกเมื่อ {redeemedAt}</span> : null}
                        {validUntil ? <span>ใช้ได้ถึง {validUntil}</span> : null}
                      </div>
                      <div className="loyalty-redemption-price">
                        {baselinePayable ? <span>ยอดก่อนใช้สิทธิ์ {formatMoney(baselinePayable.totalAmount, cart.quote?.currencyCode ?? "THB")}</span> : null}
                        {preview?.pricing ? <>
                          <span>ส่วนลด {formatMoney(preview.pricing.appliedDiscountAmount, preview.pricing.currencyCode)}</span>
                          <strong>ยอดหลังใช้สิทธิ์ {formatMoney(preview.pricing.totalAmount, preview.pricing.currencyCode)}</strong>
                        </> : preview?.error ? <strong>{preview.error}</strong> : <strong>กำลังตรวจสิทธิ์กับบิลนี้</strong>}
                      </div>
                      <div className="loyalty-redemption-actions">
                        <button
                          type="button"
                          disabled={busy || !preview?.pricing || reward.benefit_type !== "FIXED_AMOUNT_DISCOUNT"}
                          onClick={() => void confirmReward(benefit)}
                        >
                          {busy ? "กำลังใช้สิทธิ์…" : "ยืนยันใช้สิทธิ์"}
                        </button>
                      </div>
                    </section>;
                  })}
                </div>
              </section> : null}

              {promotionBenefits.length ? <section className="loyalty-redemption-resolved">
                <div className="sale-promo-heading"><strong>สิทธิ์ส่วนลดอื่น</strong><small>สิทธิ์ที่ลูกค้าเก็บไว้และยังพร้อมใช้</small></div>
                <div className="loyalty-redemption-rewards">
                  {promotionBenefits.map((benefit) => {
                    const active = claimSession.customerClaim?.claimInstanceId === benefit.source_instance_id;
                    return <section key={benefit.projection_id} className="loyalty-redemption-current">
                      <div>
                        <small>{active ? "สิทธิ์ที่ใช้กับบิลนี้แล้ว" : "สิทธิ์พร้อมใช้"}</small>
                        <strong>{promotionClaimLabel(benefit)}</strong>
                        <span>{benefit.valid_until ? `ใช้ได้ถึง ${formatBenefitTime(benefit.valid_until)}` : "พร้อมใช้"}</span>
                      </div>
                      <div className="loyalty-redemption-actions">
                        <button
                          type="button"
                          className={active ? "loyalty-redemption-release" : undefined}
                          disabled={busy || !benefit.promotion_version_id}
                          onClick={() => void (active ? detachPromotionClaim() : confirmPromotionClaim(benefit))}
                        >
                          {busy ? "กำลังตรวจสิทธิ์…" : active ? "เอาสิทธิ์ออกจากบิล" : "ยืนยันใช้สิทธิ์"}
                        </button>
                      </div>
                    </section>;
                  })}
                </div>
              </section> : null}
            </>
          )}
        </section>
      </div>,
      document.body,
    ) : null}
  </>;
}
